import { ReactNode, forwardRef, createRef, useCallback, useState, useRef } from 'react';
import { SnackbarContent, SnackbarKey, SnackbarMessage, SnackbarProvider, useSnackbar } from 'notistack';
import { css } from '@emotion/react';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import { X } from '@phosphor-icons/react';
import { Banner } from 'client/components/Banner';

const ErrorSnackbarItem = forwardRef<HTMLDivElement, { snackbarKey: SnackbarKey; message: SnackbarMessage }>(
  ({ snackbarKey: key, message }, ref) => {
    const { closeSnackbar } = useSnackbar();

    const handleDismiss = useCallback(() => closeSnackbar(key), [key]);

    return (
      <SnackbarContent ref={ref}>
        <Banner
          type="error"
          button={
            <IconButton aria-label="close" color="inherit" sx={{ p: 0.5 }} onClick={handleDismiss}>
              <X />
            </IconButton>
          }
        >
          {message}
        </Banner>
      </SnackbarContent>
    );
  }
);

export const ErrorSnackbarProvider = ({ children }: { children?: ReactNode }) => {
  const notistackRef = createRef<SnackbarProvider>();

  const renderContent = useCallback(
    (key: SnackbarKey, message: SnackbarMessage) => (<ErrorSnackbarItem snackbarKey={key} message={message} />) as ReactNode,
    []
  );

  return (
    <SnackbarProvider
      ref={notistackRef}
      maxSnack={3}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      TransitionComponent={Grow as any}
      content={renderContent}
    >
      {children}
    </SnackbarProvider>
  );
};

export interface UseErrorOptions {
  showSnackbar?: boolean;
}
export interface ErrorOptions {
  status?: string;
  errorKey?: string;
}

/** Handle displaying errors as a snackbar or a custom component */
export const useError = ({ showSnackbar = true }: UseErrorOptions = {}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [componentKey] = useState(`${Date.now() + Math.random()}`.replace('.', ''));

  const localErrorIndexRef = useRef(0);
  const localErrorKeysRef = useRef<{ [snackKey: string]: string[] }>({});

  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const [error, setError] = useState<string | JSX.Element | null>(null);
  const errorRef = useRef(error);
  errorRef.current = error;

  /** Show or hide an error message */
  const handleError = useCallback((error: string | JSX.Element | null, { status, errorKey }: ErrorOptions = {}) => {
    if (error === errorRef.current) return;

    // Show snack
    if (showSnackbarRef.current) {
      const errorComponent = (
        <span css={statusErrorStyle}>
          {error}
          {status && <span css={statusErrorStatusStyle}>{status}</span>}
        </span>
      );

      const snackKey = errorKey ? `${componentKey}.${errorKey}` : componentKey;
      const localErrorKeys = localErrorKeysRef.current;

      if (error) {
        const snackInstanceKey = `${snackKey}.${localErrorIndexRef.current++}`;

        localErrorKeys[snackKey] = [...(localErrorKeys[snackKey] || []), snackInstanceKey];

        enqueueSnackbar(errorComponent, { key: snackInstanceKey, persist: true, variant: 'error' });
      } else {
        const snackKeysToClose = (errorKey ? localErrorKeys[snackKey] : Object.values(localErrorKeys).flat()) || [];
        for (const snackInstanceKey of snackKeysToClose) {
          closeSnackbar(snackInstanceKey);
        }

        if (errorKey) {
          delete localErrorKeys[snackKey];
        } else {
          localErrorKeysRef.current = {};
        }
      }
    }

    setError(error);
  }, []);

  return {
    error,
    setError: handleError,
  };
};

const statusErrorStyle = css`
  position: relative;
  display: block;
  padding-bottom: 1.1rem;
`;

const statusErrorStatusStyle = css`
  position: absolute;
  bottom: 0;
  left: 0;
  opacity: 0.35;
`;
